import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, getPermissions, getMe } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPermissions = useCallback(async () => {
    try {
      const res = await getPermissions();
      const perms = res.data;
      setPermissions(perms);
      localStorage.setItem('boss_permissions', JSON.stringify(perms));
    } catch {
      // If permissions fail, use defaults based on stored user role
      const savedUser = localStorage.getItem('boss_user');
      if (savedUser) {
        try {
          const u = JSON.parse(savedUser);
          setPermissions({ role: u.role, permissions: u.role === 'BOSS_ADMIN' ? ['*'] : [], allowed_nav: u.role === 'BOSS_ADMIN' ? ['*'] : [] });
        } catch {}
      }
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('boss_token');
    const savedUser = localStorage.getItem('boss_user');
    const savedPerms = localStorage.getItem('boss_permissions');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        if (savedPerms) {
          setPermissions(JSON.parse(savedPerms));
        }
        // Refresh permissions in background
        loadPermissions();
      } catch {
        localStorage.removeItem('boss_token');
        localStorage.removeItem('boss_user');
        localStorage.removeItem('boss_permissions');
      }
    }
    setLoading(false);
  }, [loadPermissions]);

  const login = async (email, password) => {
    const res = await apiLogin(email, password);
    const { token, user: userData } = res.data;
    localStorage.setItem('boss_token', token);
    localStorage.setItem('boss_user', JSON.stringify(userData));
    setUser(userData);
    // Fetch permissions after login
    await loadPermissions();
    return userData;
  };

  const refreshUser = async () => {
    const res = await getMe();
    const userData = res.data;
    localStorage.setItem('boss_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('boss_token');
    localStorage.removeItem('boss_user');
    localStorage.removeItem('boss_permissions');
    setUser(null);
    setPermissions(null);
  };

  const hasPermission = (perm) => {
    if (!permissions) return false;
    if (permissions.permissions.includes('*')) return true;
    return permissions.permissions.includes(perm);
  };

  const isNavAllowed = (path) => {
    if (!permissions) return false; // Block access while permissions are loading
    if (permissions.allowed_nav.includes('*')) return true;
    return permissions.allowed_nav.some(nav => path === nav || path.startsWith(nav + '/'));
  };

  const isAdmin = () => {
    return user?.role === 'BOSS_ADMIN';
  };

  return (
    <AuthContext.Provider value={{ user, permissions, login, logout, loading, hasPermission, isNavAllowed, isAdmin, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
